import { Injector } from '@angular/core';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { InstallConfigService } from '../../install/install-config.service';
import UpsellButtonComponent from '../../upsell/upsell-button.react-component';
import { UpsellDataService } from '../../upsell/upsell-data.service';
import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationService } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { ConsumerSkuType } from '../services/consumer-license.service';
import { DocumentService } from '../services/document.service';
import { AppEvent, EventService } from '../services/event.service';
import { OfficeRouteService } from '../services/office-route.service';
import { AngularEvents } from '../shared-context/angular-events';
import { ConsumerInstallConfig, ConsumerInstallConfigService } from '../shared-context/consumer-install-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';

export class UpsellButton {
    private container: HTMLDivElement;
    private routeChangeEndSubscription: Subscription;
    private renderUpsellSubscription: Subscription;
    private isFreeUser = false;

    constructor(
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly contextService: ContextService,
        private readonly eventService: EventService,
        private readonly injector: Injector,
        private readonly officeRouteService: OfficeRouteService
    ) {
        this.consumerInstallConfigService.getConfig().then((config: ConsumerInstallConfig) => {
            const subscriptionStatus = config && config.currentSkuInfo ? config.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
            this.isFreeUser = subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NonActive;
            // only if the user is using a free version
            if (this.isFreeUser) {
                this.subscribe();

                // If free user converted to paid during a session, don't render upsell button after conversion
                this.eventService.whenEventFired(AngularEvents.convertedToPaid, ObservableScope.OnlyFuture)
                .then(() => {
                    this.isFreeUser = false;
                    const documentService = this.injector.get(DocumentService);
                    const goPremiumLink = documentService.nativeDocument.getElementById('goPremium');
                    goPremiumLink && documentService.addCssClassToElement(goPremiumLink, 'element-hidden');
                });
            }
        });
    }

    public unsubscribe() {
        if (this.routeChangeEndSubscription) {
            this.routeChangeEndSubscription.unsubscribe();
        }

        if (this.renderUpsellSubscription) {
            this.renderUpsellSubscription.unsubscribe();
        }
    }

    private subscribe(): void {
        this.renderUpsellSubscription = this.eventService.events.withScope(ObservableScope.PastAndFuture)
            .pipe(filter(event => event.name === AngularEvents.renderUpsellButton))
            .subscribe((event: AppEvent) => {
                if (event.args.length) {
                    const minimized = event.args[0] === 0;

                    if (!minimized && event.args[0] !== 1) {
                        this.container = event.args[0];
                    }

                    this.routeChangeEndSubscription = this.officeRouteService.subscribeToRouteChangeEnd(routes => {
                        if (!React || !this.isFreeUser) {
                            return;
                        }

                        // On route change, change href or toggle display
                        const currentRoute: OfficeRoute = routes.currentRoute.route;
                        const currentApp: FileType = routes.currentRoute.app;

                        ReactDOM.render(
                            <UpsellButtonComponent
                                route={currentRoute}
                                app={currentApp}
                                locService={this.injector.get(LocService)}
                                instrumentationService={this.injector.get(InstrumentationService)}
                                upsellDataService={this.injector.get(UpsellDataService)}
                                installConfigService={this.injector.get(InstallConfigService)}
                                linkTarget={this.contextService.sharedContext.defaultLinkTarget}
                                minimized={minimized}/>,
                            this.container
                        );
                    });
                }
            });
    }
}
